import React from 'react';

import {ChartConfiguration} from 'chart.js';
import {useTranslation} from 'react-i18next';

import {AuditStatisticsEntity} from '@entities/audit-statistics/entity';
import {ViolationDictionaryItem} from '@entities/violations/types';

import {ChartBox, ChartTitle} from '../styled';

import {
    createChart,
    getLocalizedTitle,
    MONTH_KEYS,
    VIOLATION_TYPES_BASE_COLORS,
    VIOLATION_TYPES_COLORS,
} from './chartUtils';

interface Props {
    statistics: AuditStatisticsEntity;
    violationTypes: ViolationDictionaryItem[];
}

export const ViolationsByTypeTrendChart = ({statistics, violationTypes}: Props) => {
    const {t, i18n} = useTranslation();
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const normalizedData = React.useMemo(() => statistics.normalizeViolationsByTypeTrend(), [
        statistics,
    ]);
    const selectedMonths = normalizedData.map((item) => item.month);
    const violationTypeCodes = Object.keys(statistics.violationsByTypeTrend);
    const labels = violationTypeCodes.map(
        (code) =>
            getLocalizedTitle(
                violationTypes.find((item) => item.code === code),
                i18n.language,
            ) || code,
    );

    React.useEffect(() => {
        if (!canvasRef.current) return undefined;

        const colors = [...VIOLATION_TYPES_COLORS];
        const config: ChartConfiguration<'line'> = {
            type: 'line',
            data: {
                labels: MONTH_KEYS.filter((_, index) =>
                    selectedMonths.includes(index + 1),
                ).map((month) => t(`dictionaries.months.${month}.short`)),
                datasets: violationTypeCodes.map((code, index) => {
                    const color = VIOLATION_TYPES_BASE_COLORS[code] ?? colors.shift();

                    return {
                        label: labels[index],
                        data: normalizedData.map((item) => item[code]),
                        cubicInterpolationMode: 'monotone',
                        fill: true,
                        backgroundColor: color?.backgroundColor,
                        borderColor: color?.borderColor,
                        borderWidth: 2,
                        pointRadius: 5,
                        pointBackgroundColor: '#fff',
                        pointHoverRadius: 5,
                        pointHoverBorderWidth: 2,
                        spanGaps: true,
                    };
                }),
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        padding: 16,
                        callbacks: {
                            label: () => '',
                            beforeLabel: (item) =>
                                `${labels[item.datasetIndex]}: ${item.formattedValue}`,
                        },
                    },
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {precision: 0},
                    },
                },
            },
        };

        const chart = createChart(canvasRef.current, config);
        return () => chart.destroy();
    }, [labels, normalizedData, selectedMonths, t, violationTypeCodes]);

    return (
        <>
            <ChartTitle style={{marginBottom: 16}}>
                {t('page_audit.page_statistic.violations-by-type-trend-title')}
            </ChartTitle>
            <ChartBox>
                <canvas ref={canvasRef} />
            </ChartBox>
        </>
    );
};
